import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'
import type {
  CreateConversationData,
  SendMessageData,
  BroadcastData,
  ConversationsListResponse,
  ConversationResponse,
  CreateConversationResponse,
  SendMessageResponse,
  BroadcastResponse,
} from '@/types/message'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed')
  }

  return data
}

export const messagesApi = {
  // List all conversations for the current user
  async getConversations(): Promise<ConversationsListResponse> {
    return fetchWithAuth<ConversationsListResponse>('/api/messages/conversations')
  },

  // Get a single conversation with messages
  async getConversation(
    conversationId: string,
    params?: { before?: string; limit?: number }
  ): Promise<ConversationResponse> {
    const searchParams = new URLSearchParams()
    if (params?.before) searchParams.set('before', params.before)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    const query = searchParams.toString()
    return fetchWithAuth<ConversationResponse>(
      `/api/messages/conversations/${conversationId}${query ? `?${query}` : ''}`
    )
  },

  // Create a new conversation
  async createConversation(data: CreateConversationData): Promise<CreateConversationResponse> {
    return fetchWithAuth<CreateConversationResponse>('/api/messages/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Send a message in a conversation
  async sendMessage(conversationId: string, data: SendMessageData): Promise<SendMessageResponse> {
    return fetchWithAuth<SendMessageResponse>(
      `/api/messages/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // Mark conversation as read
  async markAsRead(conversationId: string): Promise<void> {
    await fetchWithAuth<{ success: boolean }>(
      `/api/messages/conversations/${conversationId}/read`,
      {
        method: 'PUT',
      }
    )
  },

  // Send a broadcast message to all parents (childminder only)
  async sendBroadcast(data: BroadcastData): Promise<BroadcastResponse> {
    return fetchWithAuth<BroadcastResponse>('/api/messages/broadcast', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Search conversations and messages
  async search(query: string): Promise<{
    conversations: Array<{
      id: string
      type: string
      name: string | null
      participants: Array<{ userId: string; name: string; role: string }>
      matchingMessage: { message: string; createdAt: string }
    }>
  }> {
    const searchParams = new URLSearchParams()
    searchParams.set('q', query)
    return fetchWithAuth(`/api/messages/search?${searchParams.toString()}`)
  },
}
